import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Trash, MagnifyingGlass, Printer, Receipt, X, EnvelopeSimple, WhatsappLogo, Link as LinkIcon, Copy, ArrowSquareOut, CheckCircle, Download } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/lib/api";
import { INVOICE_STATUS_LABELS, INVOICE_STATUS_ORDER, INVOICE_STATUS_COLORS } from "@/lib/constants";
import { shareWhatsApp } from "@/lib/whatsapp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { DatePicker } from "@/components/DatePicker";
import { REPORT_DOC_SLOTS } from "@/components/ReportDocSlots";

const rupee = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);
const FIRM_FALLBACK = {
  firm_name: "Aakriti Associates", tagline: "Architectural & Engineering Consultants · Registered Valuer",
  address: "Shop No 219, Block B, Crystal Arcade, Shanker Nagar, Raipur (CG) 492001",
  gstin: "", pan: "", phone: "", email: "", logo_url: "", letterhead_note: "",
  signatory_name: "", signatory_designation: "Authorised Signatory / Registered Valuer",
};
const EMPTY = () => ({
  client_id: "none", bill_to_name: "", bill_to_address: "", bill_to_gstin: "", bill_to_email: "", job_reference: "",
  issue_date: today(), due_date: "", tax_rate: 18, notes: "", status: "draft",
  line_items: [{ description: "Property Valuation Report", qty: 1, rate: 0 }],
});

// Suggested Reference Fee slabs (per firm fee policy, Doc 8). Lower end of a range is the default.
const FEE_SLABS = [
  { max: 2500000, label: "Up to ₹25 lakh", fee: 10000 },
  { max: 5000000, label: "₹25 – 50 lakh", fee: 12500 },
  { max: 10000000, label: "₹50 lakh – 1 crore", fee: 15000 },
  { max: 50000000, label: "₹1 – 5 crore", fee: 20000, range: "₹20,000 – ₹30,000" },
  { max: 500000000, label: "₹5 – 50 crore", fee: 40000, range: "₹40,000 – ₹1,00,000" },
  { max: Infinity, label: "Above ₹50 crore", pct: 0.0002, min: 125000, range: "0.02% (min ₹1,25,000)" },
];
function suggestedFee(mv) {
  const v = Number(mv) || 0;
  const slab = FEE_SLABS.find((x) => v <= x.max) || FEE_SLABS[FEE_SLABS.length - 1];
  const fee = slab.pct ? Math.max(slab.min, Math.round(v * slab.pct)) : slab.fee;
  return { fee: fee || 0, slab };
}
// Additional charges catalog (chargeable separately, Doc 6). pct items compute off the valuation fee.
const ADDL_CHARGES = [
  { label: "Outstation visit (beyond city limits)", desc: "Outstation visit — actual travel + ₹5,000/day professional allowance", type: "fixed", rate: 5000 },
  { label: "Urgent report (within 24 hours)", desc: "Urgent report surcharge — 25% of assignment fee", type: "pct", pct: 0.25 },
  { label: "Re-inspection", desc: "Re-inspection — 50% of original fee", type: "pct", pct: 0.5 },
  { label: "Stage inspection", desc: "Stage inspection — 50% of original fee", type: "pct", pct: 0.5 },
  { label: "Joint inspection with bank/legal team", desc: "Joint inspection with bank / legal team", type: "fixed", rate: 5000 },
  { label: "Drone survey", desc: "Drone survey — actual cost + professional supervision", type: "fixed", rate: 0 },
  { label: "DGPS survey", desc: "DGPS survey — actual cost", type: "fixed", rate: 0 },
  { label: "GIS mapping", desc: "GIS mapping — as applicable, quoted separately", type: "fixed", rate: 0 },
  { label: "Court appearance / expert testimony", desc: "Court appearance / expert testimony — ₹25,000/day + travel & accommodation", type: "fixed", rate: 25000 },
  { label: "Multiple properties in one assignment", desc: "Additional property — independent fee per property", type: "fixed", rate: 0 },
];

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [params, setParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY());
  const [view, setView] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [payEnabled, setPayEnabled] = useState(false);
  const [sendingReceipt, setSendingReceipt] = useState(false);
  const [firm, setFirm] = useState(FIRM_FALLBACK);
  const [jobs, setJobs] = useState([]);
  const [marketValue, setMarketValue] = useState("");
  const [reportDocs, setReportDocs] = useState([]);

  const load = () => api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/payments/config").then((r) => setPayEnabled(!!r.data.enabled)).catch(() => {});
    api.get("/settings/firm").then((r) => setFirm({ ...FIRM_FALLBACK, ...(r.data || {}) })).catch(() => {});
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!params.get("new")) return;
    const f = EMPTY();
    const cid = params.get("client");
    if (cid && clients.length) {
      const c = clients.find((x) => x.id === cid);
      if (c) { f.client_id = cid; f.bill_to_name = c.name || ""; f.bill_to_address = c.address || ""; f.bill_to_gstin = c.gstin || ""; f.bill_to_email = c.email || ""; }
    }
    const jref = params.get("job");
    if (jref) f.job_reference = jref;
    setForm(f); setMarketValue(""); setOpen(true);
    if (clients.length) setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, clients]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const setItem = (i, k, v) => { const li = [...form.line_items]; li[i] = { ...li[i], [k]: v }; setForm({ ...form, line_items: li }); };
  const addItem = () => setForm({ ...form, line_items: [...form.line_items, { description: "", qty: 1, rate: 0 }] });
  const removeItem = (i) => setForm({ ...form, line_items: form.line_items.filter((_, idx) => idx !== i) });

  const onClient = (id) => {
    if (id === "none") { setForm({ ...form, client_id: "none" }); return; }
    const c = clients.find((x) => x.id === id);
    setForm({ ...form, client_id: id, job_reference: "", bill_to_name: c?.name || "", bill_to_address: c?.address || "", bill_to_gstin: c?.gstin || "", bill_to_email: c?.email || "" });
  };
  const clientJobs = jobs.filter((j) => form.client_id && form.client_id !== "none" && j.client_id === form.client_id);
  const onPickJob = (ref) => {
    if (ref === "none") { setForm({ ...form, job_reference: "" }); return; }
    const j = jobs.find((x) => x.reference === ref);
    const fmv = (j?.valuation_details || {}).fmv;
    const next = { ...form, job_reference: ref };
    if (fmv) {
      const fee = suggestedFee(fmv).fee;
      const li = [...form.line_items];
      const first = li[0] || { description: "", qty: 1, rate: 0 };
      li[0] = { ...first, description: first.description || "Professional valuation fee", rate: fee };
      next.line_items = li;
      setMarketValue(String(fmv));
    }
    setForm(next);
  };

  const subtotal = form.line_items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.rate) || 0), 0);
  const taxAmt = subtotal * (Number(form.tax_rate) || 0) / 100;
  const total = subtotal + taxAmt;

  const matchedJob = jobs.find((j) => (j.reference || "").trim().toLowerCase() === (form.job_reference || "").trim().toLowerCase());
  useEffect(() => {
    if (matchedJob) { const v = (matchedJob.valuation_details || {}).fmv; if (v) setMarketValue(String(v)); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchedJob?.id]);

  useEffect(() => {
    if (!matchedJob?.id) { setReportDocs([]); return; }
    api.get(`/jobs/${matchedJob.id}/documents`).then(({ data }) => setReportDocs(Array.isArray(data) ? data : [])).catch(() => setReportDocs([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchedJob?.id]);
  const addReportLine = (label) => { setForm((f) => ({ ...f, line_items: [...f.line_items, { description: label, qty: 1, rate: 0 }] })); toast.success(`Added line: ${label}`); };

  const sf = suggestedFee(marketValue);
  const baseFee = () => Number(form.line_items?.[0]?.rate) || 0;
  const applyRefFee = () => {
    const li = [...form.line_items];
    const first = li[0] || { description: "", qty: 1, rate: 0 };
    const isValnLine = !first.description || /valuation/i.test(first.description);
    if (li.length && isValnLine) li[0] = { ...first, description: first.description || "Professional valuation fee", rate: sf.fee };
    else li.unshift({ description: "Professional valuation fee", qty: 1, rate: sf.fee });
    setForm({ ...form, line_items: li });
    toast.success(`Applied suggested fee ${rupee(sf.fee)}`);
  };
  const addCharge = (key) => {
    const c = ADDL_CHARGES.find((x) => x.label === key);
    if (!c) return;
    const rate = c.type === "pct" ? Math.round(baseFee() * c.pct) : (c.rate || 0);
    setForm({ ...form, line_items: [...form.line_items, { description: c.desc, qty: 1, rate }] });
    toast.success(`Added: ${c.label}`);
  };

  const openNew = () => { setForm(EMPTY()); setMarketValue(""); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.bill_to_name) { toast.error("Enter who the invoice is billed to"); return; }
    try {
      const payload = {
        ...form,
        client_id: form.client_id === "none" ? null : form.client_id,
        tax_rate: Number(form.tax_rate) || 0,
        due_date: form.due_date || null,
        line_items: form.line_items.map((it) => ({ description: it.description, qty: Number(it.qty) || 0, rate: Number(it.rate) || 0 })),
      };
      await api.post("/invoices", payload);
      toast.success("Invoice created");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const setStatus = async (inv, status) => {
    try {
      await api.put(`/invoices/${inv.id}`, {
        client_id: inv.client_id, bill_to_name: inv.bill_to_name, bill_to_address: inv.bill_to_address,
        bill_to_gstin: inv.bill_to_gstin, bill_to_email: inv.bill_to_email, job_reference: inv.job_reference, issue_date: inv.issue_date,
        due_date: inv.due_date, tax_rate: inv.tax_rate, notes: inv.notes, status, line_items: inv.line_items,
      });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/invoices/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const emailInvoice = async (inv) => {
    if (!inv.bill_to_email) { toast.error("Add a billing email to this invoice first (edit not available — recreate with email)"); return; }
    try { await api.post(`/invoices/${inv.id}/send-email`); toast.success(`Invoice emailed to ${inv.bill_to_email}`); load(); setView(null); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const whatsappInvoice = (inv) => {
    const link = `${window.location.origin}/pay/${inv.id}?token=${inv.public_token || ""}`;
    const msg = `*Invoice ${inv.invoice_number}* from Aakriti Associates\nBilled to: ${inv.bill_to_name}\nAmount: ${rupee(inv.total)} (incl. ${inv.tax_rate}% GST)\nView & pay online: ${link}`;
    shareWhatsApp("", msg);
  };

  const genLink = async (inv) => {
    try {
      const { data } = await api.post(`/invoices/${inv.id}/payment-link`);
      setView((v) => (v && v.id === inv.id ? { ...v, payment_link_url: data.payment_link_url, payment_link_id: data.payment_link_id, status: v.status === "draft" ? "sent" : v.status } : v));
      load();
      toast.success("Razorpay payment link ready");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const copyLink = (url) => { navigator.clipboard?.writeText(url); toast.success("Link copied to clipboard"); };

  const rotateLink = async (inv) => {
    if (!window.confirm("Revoke the current shared link and issue a new one? Any previously shared invoice/receipt links will stop working.")) return;
    try {
      const { data } = await api.post(`/invoices/${inv.id}/rotate-token`);
      setView((v) => (v && v.id === inv.id ? { ...v, public_token: data.public_token, payment_link_url: "", payment_link_id: "" } : v));
      load();
      toast.success("Old link revoked — a fresh secure link is now active");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const resendReceipt = async (inv) => {
    setSendingReceipt(true);
    try {
      const { data } = await api.post(`/invoices/${inv.id}/resend-receipt`);
      toast.success(`Receipt emailed to ${data.to}`);
      const stamp = new Date().toISOString();
      setView((v) => (v && v.id === inv.id ? { ...v, receipt_sent_at: stamp } : v));
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSendingReceipt(false); }
  };

  const whatsappPayLink = (inv) => {
    const msg = `*Invoice ${inv.invoice_number}* from Aakriti Associates\nBilled to: ${inv.bill_to_name}\nAmount: ${rupee(inv.total)} (incl. ${inv.tax_rate}% GST)\nPay securely online: ${inv.payment_link_url}`;
    shareWhatsApp("", msg);
  };

  const filtered = invoices.filter((i) => {
    const q = search.toLowerCase();
    const matchQ = !q || `${i.invoice_number} ${i.bill_to_name} ${i.job_reference}`.toLowerCase().includes(q);
    const matchS = statusFilter === "all" || i.status === statusFilter;
    const issued = (i.issue_date || "").slice(0, 10);
    const matchDate = (!fromDate || (issued && issued >= fromDate)) && (!toDate || (issued && issued <= toDate));
    return matchQ && matchS && matchDate;
  });

  const totalOutstanding = invoices.filter((i) => i.status === "sent").reduce((s, i) => s + (i.total || 0), 0);
  const totalPaid = invoices.filter((i) => i.status === "paid").reduce((s, i) => s + (i.total || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-accent">Billing</div>
          <h1 className="font-display font-black text-3xl md:text-4xl text-primary mt-1">Invoices</h1>
          <p className="text-sm text-muted-foreground mt-1">{invoices.length} invoices · <span className="text-emerald-600">{rupee(totalPaid)} collected</span> · <span className="text-amber-600">{rupee(totalOutstanding)} outstanding</span></p>
        </div>
        <Button onClick={openNew} data-testid="add-invoice-btn" className="rounded-full bg-primary hover:bg-[#113A64]"><Plus className="mr-1" size={16} /> New Invoice</Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search number, client, job ref…" className="pl-9" data-testid="invoice-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[170px]" data-testid="invoice-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Statuses</SelectItem>{INVOICE_STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{INVOICE_STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
        </Select>
        <DatePicker value={fromDate} onChange={setFromDate} placeholder="From date" clearable className="w-[150px]" data-testid="invoice-from-date" />
        <DatePicker value={toDate} onChange={setToDate} placeholder="To date" clearable className="w-[150px]" data-testid="invoice-to-date" />
        {(fromDate || toDate) && <Button variant="ghost" size="sm" className="text-xs" onClick={() => { setFromDate(""); setToDate(""); }} data-testid="invoice-date-clear">Clear dates</Button>}
      </div>

      <Card className="border-border overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-muted">
              <TableRow>
                <TableHead className="text-xs uppercase tracking-wide">Invoice #</TableHead>
                <TableHead className="text-xs uppercase tracking-wide">Bill To</TableHead>
                <TableHead className="text-xs uppercase tracking-wide">Issued</TableHead>
                <TableHead className="text-xs uppercase tracking-wide text-right">Total</TableHead>
                <TableHead className="text-xs uppercase tracking-wide">Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((inv) => (
                <TableRow key={inv.id} className="hover:bg-muted/40 cursor-pointer" data-testid={`invoice-row-${inv.id}`}>
                  <TableCell className="font-mono text-xs text-primary" onClick={() => setView(inv)}>{inv.invoice_number}</TableCell>
                  <TableCell onClick={() => setView(inv)}><div className="font-medium text-sm text-primary">{inv.bill_to_name}</div>{inv.job_reference && <div className="text-xs text-muted-foreground font-mono">{inv.job_reference}</div>}</TableCell>
                  <TableCell className="text-xs font-mono text-muted-foreground" onClick={() => setView(inv)}>{(inv.issue_date || "").slice(0, 10)}</TableCell>
                  <TableCell className="text-right font-mono text-sm text-primary" onClick={() => setView(inv)}>{rupee(inv.total)}</TableCell>
                  <TableCell>
                    <Select value={inv.status} onValueChange={(v) => setStatus(inv, v)}>
                      <SelectTrigger className={`h-8 text-xs w-[110px] border ${INVOICE_STATUS_COLORS[inv.status]}`} data-testid={`invoice-status-${inv.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{INVOICE_STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{INVOICE_STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-primary" onClick={() => setView(inv)} data-testid={`view-invoice-${inv.id}`}><Receipt size={16} /></Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" data-testid={`delete-invoice-${inv.id}`}><Trash size={15} /></Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Delete invoice?</AlertDialogTitle><AlertDialogDescription>Remove {inv.invoice_number}?</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(inv.id)} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">No invoices yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">New Invoice</DialogTitle><DialogDescription>Create a GST invoice for a client or valuation job.</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="invoice-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Client (optional)</Label>
                <Select value={form.client_id} onValueChange={onClient}><SelectTrigger data-testid="invoice-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— Manual entry —</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-1.5"><Label>Job {clientJobs.length > 0 ? "(auto-fills fee)" : "Reference (optional)"}</Label>
                {clientJobs.length > 0 ? (
                  <Select value={form.job_reference || "none"} onValueChange={onPickJob}>
                    <SelectTrigger data-testid="invoice-job-select"><SelectValue placeholder="Select a job to auto-fill the fee" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {clientJobs.map((j) => <SelectItem key={j.id} value={j.reference}>{j.reference}{j.applicant_name ? ` · ${j.applicant_name}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={form.job_reference} onChange={set("job_reference")} placeholder="VAL-..." data-testid="invoice-job-ref" />
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Bill To *</Label><Input required value={form.bill_to_name} onChange={set("bill_to_name")} data-testid="invoice-billto" /></div>
              <div className="space-y-1.5"><Label>GSTIN</Label><Input value={form.bill_to_gstin} onChange={set("bill_to_gstin")} /></div>
            </div>
            <div className="space-y-1.5"><Label>Billing Email</Label><Input type="email" value={form.bill_to_email} onChange={set("bill_to_email")} placeholder="client@email.com (for emailing the invoice)" data-testid="invoice-email" /></div>
            <div className="space-y-1.5"><Label>Billing Address</Label><Textarea value={form.bill_to_address} onChange={set("bill_to_address")} rows={2} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Issue Date</Label><DatePicker value={form.issue_date} onChange={set("issue_date")} placeholder="Select date" className="w-full" data-testid="invoice-issue-date" /></div>
              <div className="space-y-1.5"><Label>Due Date</Label><DatePicker value={form.due_date} onChange={set("due_date")} placeholder="Select date" className="w-full" data-testid="invoice-due-date" /></div>
              <div className="space-y-1.5"><Label>GST %</Label><Input type="number" value={form.tax_rate} onChange={set("tax_rate")} data-testid="invoice-tax" /></div>
            </div>

            <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-3" data-testid="fee-structure-panel">
              <div className="grid grid-cols-2 gap-3 items-end">
                <div className="space-y-1.5">
                  <Label className="text-xs">Property Market Value — FMV (₹){matchedJob ? " · auto-pulled from job" : ""}</Label>
                  <Input type="number" value={marketValue} onChange={(e) => setMarketValue(e.target.value)} placeholder="e.g. 4500000" data-testid="fee-market-value" />
                </div>
                <div className="text-xs space-y-0.5">
                  <div className="text-muted-foreground">{sf.slab.label}</div>
                  <div className="font-semibold text-primary">Suggested reference fee: {sf.slab.range || rupee(sf.fee)}</div>
                  <Button type="button" size="sm" variant="outline" className="mt-1 h-7 text-xs" onClick={applyRefFee} disabled={!Number(marketValue)} data-testid="apply-ref-fee-btn">Apply {rupee(sf.fee)} as valuation fee</Button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Add an additional charge (chargeable separately)</Label>
                <Select value="" onValueChange={addCharge}>
                  <SelectTrigger data-testid="addl-charge-select"><SelectValue placeholder="Select a charge to add as a line item" /></SelectTrigger>
                  <SelectContent>{ADDL_CHARGES.map((c) => <SelectItem key={c.label} value={c.label}>{c.label}{c.type === "pct" ? ` (${c.pct * 100}% of fee)` : ""}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground">Percentage charges auto-compute from the valuation fee above; actual-cost items are added at ₹0 for you to fill.</p>
              </div>
            </div>

            {matchedJob && (
              <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2" data-testid="invoice-report-docs-panel">
                <Label className="text-xs">Supporting reports delivered for {matchedJob.reference}</Label>
                <div className="space-y-1.5">
                  {REPORT_DOC_SLOTS.map((s) => {
                    const files = reportDocs.filter((d) => d.doc_type === s.type);
                    const delivered = files.length > 0;
                    const billed = form.line_items.some((li) => (li.description || "").trim() === s.label);
                    return (
                      <div key={s.type} className="flex items-center gap-2 text-sm" data-testid={`invoice-report-${s.type}`}>
                        {delivered ? <CheckCircle size={16} weight="fill" className="text-emerald-600 shrink-0" /> : <X size={16} className="text-muted-foreground/50 shrink-0" />}
                        <span className={delivered ? "text-primary" : "text-muted-foreground"}>{s.label}</span>
                        {delivered && <span className="text-[10px] text-muted-foreground truncate">· {files.map((f) => f.filename).join(", ")}</span>}
                        <div className="ml-auto shrink-0">
                          {delivered && (billed
                            ? <span className="text-[10px] text-emerald-700 font-semibold">On invoice</span>
                            : <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => addReportLine(s.label)} data-testid={`invoice-bill-${s.type}`}>Bill this</Button>)}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-muted-foreground">Tick = report uploaded on the job. Click "Bill this" to add it as a line item, then enter the amount below.</p>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between"><Label>Line Items</Label><Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="add-line-item"><Plus size={14} className="mr-1" /> Add</Button></div>
              {form.line_items.map((it, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} placeholder="Description" className="flex-1" data-testid={`line-desc-${i}`} />
                  <Input type="number" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} placeholder="Qty" className="w-16" data-testid={`line-qty-${i}`} />
                  <Input type="number" value={it.rate} onChange={(e) => setItem(i, "rate", e.target.value)} placeholder="Rate" className="w-28" data-testid={`line-rate-${i}`} />
                  <div className="w-28 text-right font-mono text-sm text-muted-foreground">{rupee((Number(it.qty) || 0) * (Number(it.rate) || 0))}</div>
                  {form.line_items.length > 1 && <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-destructive shrink-0" onClick={() => removeItem(i)}><X size={14} /></Button>}
                </div>
              ))}
            </div>

            <div className="flex justify-end">
              <div className="w-56 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">{rupee(subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">GST ({form.tax_rate || 0}%)</span><span className="font-mono">{rupee(taxAmt)}</span></div>
                <div className="flex justify-between border-t border-border pt-1 font-semibold text-primary"><span>Total</span><span className="font-mono" data-testid="invoice-total">{rupee(total)}</span></div>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea value={form.notes} onChange={set("notes")} rows={2} placeholder="Payment terms, bank details…" /></div>
            <DialogFooter><Button type="submit" className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="save-invoice-btn">Create Invoice</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View / Print dialog */}
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader className="no-print"><DialogTitle className="font-display text-2xl">Invoice</DialogTitle><DialogDescription>Preview and print this invoice.</DialogDescription></DialogHeader>
          {view && (
            <div>
              <div data-print-area className="bg-white p-8 border border-border rounded-md">
                <div className="flex justify-between items-start border-b-2 border-accent pb-5">
                  <div>
                    <div className="font-display font-black text-2xl text-primary">{firm.firm_name}</div>
                    <div className="text-xs text-muted-foreground mt-1">{firm.tagline}</div>
                    <div className="text-xs text-muted-foreground">{firm.address}</div>
                    {(firm.phone || firm.email) && <div className="text-xs text-muted-foreground">{[firm.phone, firm.email].filter(Boolean).join(" · ")}</div>}
                    {(firm.gstin || firm.pan) && (
                      <div className="text-xs font-semibold text-primary mt-1" data-testid="invoice-firm-gstin">
                        {[firm.gstin && `GSTIN ${firm.gstin}`, firm.pan && `PAN ${firm.pan}`].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </div>
                  <div className="text-right shrink-0 pl-4">
                    {firm.logo_url && <img src={firm.logo_url} alt={firm.firm_name} className="h-12 ml-auto object-contain" data-testid="invoice-firm-logo" />}
                    <div className="font-display text-xl text-primary mt-2">INVOICE</div>
                    <div className="font-mono text-sm text-muted-foreground mt-1">{view.invoice_number}</div>
                    <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded border ${INVOICE_STATUS_COLORS[view.status]}`}>{INVOICE_STATUS_LABELS[view.status]}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 py-5 text-sm">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Bill To</div>
                    <div className="font-medium text-primary">{view.bill_to_name}</div>
                    {view.bill_to_address && <div className="text-muted-foreground whitespace-pre-line">{view.bill_to_address}</div>}
                    {view.bill_to_gstin && <div className="text-muted-foreground">GSTIN: {view.bill_to_gstin}</div>}
                  </div>
                  <div className="text-right">
                    <div><span className="text-muted-foreground">Issue Date: </span><span className="font-mono">{(view.issue_date || "").slice(0, 10)}</span></div>
                    {view.due_date && <div><span className="text-muted-foreground">Due Date: </span><span className="font-mono">{view.due_date.slice(0, 10)}</span></div>}
                    {view.job_reference && <div><span className="text-muted-foreground">Job Ref: </span><span className="font-mono">{view.job_reference}</span></div>}
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead><tr className="bg-muted text-left"><th className="py-2 px-3 text-xs uppercase tracking-wide">Description</th><th className="py-2 px-3 text-xs uppercase tracking-wide text-right">Qty</th><th className="py-2 px-3 text-xs uppercase tracking-wide text-right">Rate</th><th className="py-2 px-3 text-xs uppercase tracking-wide text-right">Amount</th></tr></thead>
                  <tbody>
                    {(view.line_items || []).map((it, i) => (
                      <tr key={i} className="border-b border-border"><td className="py-2 px-3">{it.description}</td><td className="py-2 px-3 text-right font-mono">{it.qty}</td><td className="py-2 px-3 text-right font-mono">{rupee(it.rate)}</td><td className="py-2 px-3 text-right font-mono">{rupee((it.qty || 0) * (it.rate || 0))}</td></tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex justify-end mt-4">
                  <div className="w-60 space-y-1 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">{rupee(view.subtotal)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">GST ({view.tax_rate}%)</span><span className="font-mono">{rupee(view.tax_amount)}</span></div>
                    <div className="flex justify-between border-t border-border pt-1.5 font-bold text-primary text-base"><span>Total</span><span className="font-mono">{rupee(view.total)}</span></div>
                  </div>
                </div>
                {view.notes && <div className="mt-6 pt-4 border-t border-border text-xs text-muted-foreground whitespace-pre-line"><span className="font-medium text-primary">Notes: </span>{view.notes}</div>}
                <div className="mt-8 flex justify-between items-end gap-6" data-testid="invoice-signature-block">
                  <div className="text-xs text-muted-foreground max-w-[55%]">
                    {firm.letterhead_note || "This invoice is raised against valuation services rendered. Please quote the invoice number with your payment."}
                  </div>
                  <div className="text-xs text-right">
                    <div className="text-primary">For <span className="font-semibold">{firm.firm_name}</span></div>
                    <div className="h-12" />
                    <div className="border-t border-slate-400 pt-1 w-52 ml-auto">
                      {firm.signatory_name && <div className="font-semibold text-primary">{firm.signatory_name}</div>}
                      <div className="text-muted-foreground">{firm.signatory_designation}</div>
                    </div>
                  </div>
                </div>
                <div className="mt-6 pt-3 border-t border-border text-center text-[11px] text-muted-foreground">
                  {[firm.firm_name, firm.address, firm.gstin && `GSTIN ${firm.gstin}`].filter(Boolean).join(" · ")}
                </div>
              </div>
              {view.status === "paid" && (
                <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4 no-print" data-testid="invoice-paid-note">
                  <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700"><CheckCircle size={16} weight="fill" /> This invoice has been paid.</div>
                  <p className="mt-1 text-xs text-emerald-700/80" data-testid="receipt-status">
                    {view.receipt_sent_at ? `Receipt emailed to ${view.bill_to_email || "client"} on ${new Date(view.receipt_sent_at).toLocaleString("en-IN")}.` : "No receipt has been emailed for this payment yet."}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => window.open(`${API}/public/receipts/${view.id}/pdf?token=${view.public_token || ""}`, "_blank")} data-testid="download-receipt-pdf"><Download size={14} className="mr-1" /> Download PDF Receipt</Button>
                    <Button type="button" size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" disabled={sendingReceipt} onClick={() => resendReceipt(view)} data-testid="resend-receipt-btn"><EnvelopeSimple size={14} className="mr-1" /> {sendingReceipt ? "Sending…" : view.receipt_sent_at ? "Resend Receipt" : "Email Receipt"}</Button>
                  </div>
                </div>
              )}
              {view.status !== "paid" && payEnabled && (
                <div className="mt-4 rounded-lg border border-accent/30 bg-accent/5 p-4 no-print" data-testid="payment-link-panel">
                  <div className="text-xs tracking-[0.15em] uppercase font-bold text-accent mb-2 flex items-center gap-1.5"><LinkIcon size={14} /> Online Payment Link</div>
                  {view.payment_link_url ? (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input readOnly value={view.payment_link_url} onFocus={(e) => e.target.select()} className="text-xs font-mono" data-testid="payment-link-url" />
                        <Button type="button" variant="outline" size="sm" className="rounded-full shrink-0" onClick={() => copyLink(view.payment_link_url)} data-testid="copy-payment-link"><Copy size={14} className="mr-1" /> Copy</Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => window.open(view.payment_link_url, "_blank")} data-testid="open-payment-link"><ArrowSquareOut size={14} className="mr-1" /> Open</Button>
                        <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => whatsappPayLink(view)} data-testid="wa-payment-link"><WhatsappLogo size={14} className="mr-1" /> Send on WhatsApp</Button>
                      </div>
                    </div>
                  ) : (
                    <Button type="button" size="sm" className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold" onClick={() => genLink(view)} data-testid="generate-payment-link"><LinkIcon size={14} className="mr-1" /> Generate Razorpay Payment Link</Button>
                  )}
                </div>
              )}
              <div className="flex flex-wrap justify-end gap-2 mt-4 no-print">
                <Button variant="outline" onClick={() => rotateLink(view)} className="rounded-full" data-testid="rotate-link-btn"><LinkIcon size={16} className="mr-1" /> Revoke &amp; New Link</Button>
                <Button variant="outline" onClick={() => whatsappInvoice(view)} className="rounded-full" data-testid="whatsapp-invoice-btn"><WhatsappLogo size={16} className="mr-1" /> WhatsApp</Button>
                <Button variant="outline" onClick={() => emailInvoice(view)} className="rounded-full" data-testid="email-invoice-btn"><EnvelopeSimple size={16} className="mr-1" /> Email</Button>
                <Button onClick={() => window.print()} className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold" data-testid="print-invoice-btn"><Printer size={16} className="mr-1" /> Print / PDF</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
